import React from "react";
import { ReactComponent as Droplets } from "../../../../../assects/svg/client_details/drop.svg";
import { WaterCycle } from "../../../hooks/useAllLogsData";
import useCustomScrollbar from "../../../../styles/useCustomScrollbar";

interface WaterIntakeCardProps {
  waterCycle: WaterCycle[];
}

const WaterIntakeCard: React.FC<WaterIntakeCardProps> = ({ waterCycle }) => {
  // Get today's water data (should be first item in array)
  const todayWater = waterCycle.length > 0 ? waterCycle[0] : null;

  if (!todayWater) {
    return (
      <div className="bg-white rounded-md shadow-sm p-4 sticky top-6 flex flex-col h-full">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Droplets className="w-5 h-5 text-[#20BCBB]" />
            <h3 className="text-xs 3xl:text-[14px] text-[#1F1F1F] font-semibold font-inter uppercase">
              WATER INTAKE
            </h3>
          </div>
          <span className="text-xs 3xl:text-[14px] text-[#1F1F1F] font-inter font-medium">
            0/0L
          </span>
        </div>
        <div className=" flex items-center justify-center  ">
          <p className="text-sm font-medium text-[#646464] font-inter">No entries yet</p>
        </div>
      </div>
    );
  }

  // ===========================================================================
  // OLD RESPONSE — comment this block + uncomment NEW block to switch
  // WaterCycle shape: { target_water: { unit, value, value_ml }, total_intake_ml, water_logs[] }
  // ===========================================================================
  if (!todayWater.target_water) {
    return (
      <div className="bg-white rounded-md shadow-sm p-4 sticky top-6 flex flex-col h-full">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Droplets className="w-5 h-5 text-[#20BCBB]" />
            <h3 className="text-xs 3xl:text-[14px] text-[#1F1F1F] font-semibold font-inter uppercase">
              WATER INTAKE
            </h3>
          </div>
          <span className="text-xs 3xl:text-[14px] text-[#1F1F1F] font-inter font-medium">0/0L</span>
        </div>
        <div className="flex items-center justify-center">
          <p className="text-sm font-medium text-[#646464] font-inter">No entries yet</p>
        </div>
      </div>
    );
  }

  const targetLitres = todayWater.target_water.value;
  const intakeLitres = (todayWater.total_intake_ml / 1000).toFixed(1);
  const progressPercentage = todayWater.target_water.value_to_ml > 0
  ? (todayWater.total_intake_ml / todayWater.target_water.value_to_ml) * 100
  : 0;


  // Format time from "HH:MM:SS.mmm" to "HH:MM AM/PM"
  const formatTime = (timeStr: string): string => {
  // API returns "HH:MM:SS AM/PM" — parse it directly
  const parts = timeStr.trim().split(" ");
  if (parts.length === 2) {
    // Already has AM/PM suffix e.g. "04:24:51 PM"
    const [time, period] = parts;
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour.toString().padStart(2, "0")}:${minutes} ${period}`;
  }
  // Fallback: treat as 24h "HH:MM:SS"
  const [hours, minutes] = timeStr.split(":");
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? "PM" : "AM";
  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  return `${displayHour.toString().padStart(2, "0")}:${minutes} ${ampm}`;
};
  // ===========================================================================

  // ===========================================================================
  // NEW RESPONSE — comment this block + uncomment OLD block to revert
  // WaterCycle shape: flat — { unit, value (target in L as string), total_intake (ml), no water_logs }
  // ===========================================================================
  // const targetLitres = parseFloat(todayWater.value);
  // const intakeLitres = (todayWater.total_intake / 1000).toFixed(1);
  // const targetMl = targetLitres * 1000;
  // const progressPercentage = targetMl > 0 ? (todayWater.total_intake / targetMl) * 100 : 0;
  // ===========================================================================

  return (
    <div className="bg-white rounded-md shadow-sm p-4 sticky top-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Droplets className="w-5 h-5 text-[#20BCBB]" />
          <h3 className="text-xs 3xl:text-[14px] text-[#1F1F1F] font-semibold font-inter uppercase">
            WATER INTAKE
          </h3>
        </div>
        <span className="text-xs 3xl:text-[14px] text-[#1F1F1F] font-inter font-medium">
          {intakeLitres}/{targetLitres}L
        </span>
      </div>

      {/* Progress Bar */}
      <div className="h-1 bg-gray-200 rounded-full mb-4">
        <div
          className="h-full bg-[#20BCBB] rounded-full transition-all duration-300"
          style={{ width: `${Math.min(progressPercentage, 100)}%` }}
        ></div>
      </div>

      {/* =======================================================================
          OLD RESPONSE — Scrollable logs list (comment out for NEW, uncomment for OLD) */}

      <div className="max-h-[260px] overflow-y-auto custom-scrollbar-overlay">
        {todayWater.water_logs.length === 0 ? (
          <div className="flex items-center justify-center py-28">
            <p className="text-sm font-medium text-[#646464] font-inter">No entries yet</p>
          </div>
        ) : (
          todayWater.water_logs.map((log, i) => (
            <div key={i} className="flex justify-between py-2.5 pr-2 text-sm">
              <span className="text-[14px] 3xl:text-[16px] font-medium font-inter text-[#1F1F1F]">
                {log.intake_ml} ml
              </span>
              <span className="text-[14px] 3xl:text-[16px] font-inter text-[#646464]">
                {formatTime(log.intake_time)}
              </span>
            </div>
          ))
        )}
      </div>

      {/* =======================================================================
          NEW RESPONSE — No water_logs in response; show total intake summary only
      <div className="flex items-center justify-center py-4">
        <p className="text-[14px] 3xl:text-[16px] font-medium font-inter text-[#646464]">
          {todayWater.total_intake > 0
            ? `${todayWater.total_intake} ml consumed`
            : "No entries yet"}
        </p>
      </div>
          ======================================================================= */}
    </div>
  );
};

export default WaterIntakeCard;